import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useDocument } from '@/hooks/firestore';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Textarea, Select, Input } from '@/components/ui/Input';
import { LocationPicker, type LocationValue } from '@/components/ui/LocationPicker';
import { PriceBreakdown } from './PriceBreakdown';
import { BookingSlotPicker } from './BookingSlotPicker';
import { SellerSafetyBadges } from '@/components/seller/SellerSafetyBadges';
import { toast } from '@/components/ui/Toast';
import { callCreateBooking, callRedeemPromoCode } from '@/lib/callables';
import { useAuth } from '@/stores/auth';
import {
  LATE_NIGHT_END_HOUR,
  LATE_NIGHT_START_HOUR,
  PAYMENTS_ENABLED,
  PLATFORM_COMMISSION_PCT,
  formatZAR,
  getSharedSpots,
  isLateNightSlot,
  type BookingLocation,
  type BookingPricing,
  type OperatesFrom,
  type PublicProfileDoc,
  type ServiceDoc,
} from '@makhe/shared';

interface Props {
  open: boolean;
  onClose: () => void;
  service: ServiceDoc;
  initialTier: string;
}

export function BookingSheet({ open, onClose, service, initialTier }: Props) {
  const { userDoc } = useAuth();
  const navigate = useNavigate();
  const sellerRef = doc(db, 'publicProfiles', service.sellerId);
  const { data: sellerUser } = useDocument<PublicProfileDoc>(sellerRef);
  const [tier, setTier] = useState<string>(initialTier);
  const [slotMs, setSlotMs] = useState<number | null>(null);
  const [urgent, setUrgent] = useState(false);
  const [locType, setLocType] = useState<OperatesFrom>(service.operatesFrom[0] ?? 'seller_location');
  const [coord, setCoord] = useState<LocationValue | null>(null);
  const [locNotes, setLocNotes] = useState('');
  const [residence, setResidence] = useState('');
  const [customNotes, setCustomNotes] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoPreview, setPromoPreview] = useState<{
    discount: number;
    total: number;
    applied: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);

  const tierSpec = useMemo(
    () => service.pricingTiers.find((t) => t.tier === tier),
    [service, tier],
  );

  const previewPricing: BookingPricing | null = useMemo(() => {
    if (!tierSpec || !slotMs) return null;
    const start = new Date(slotMs);
    const hour = start.getHours();
    const isAfterHours = hour >= LATE_NIGHT_START_HOUR || hour < LATE_NIGHT_END_HOUR;
    const surchargePct = service.filters.afterHoursSurchargePct ?? 0;
    const base = tierSpec.priceZARcents;
    const ah = isAfterHours && surchargePct > 0 ? Math.round((base * surchargePct) / 100) : 0;
    const promoDiscount = promoPreview?.discount ?? 0;
    const total = Math.max(0, base + ah - promoDiscount);
    const commission = Math.round((total * PLATFORM_COMMISSION_PCT) / 100);
    return {
      baseZARcents: base,
      afterHoursSurchargeZARcents: ah,
      totalZARcents: total,
      commissionZARcents: commission,
      sellerPayoutZARcents: total - commission,
      ...(promoDiscount > 0
        ? { promoDiscountZARcents: promoDiscount, promoCodeApplied: promoPreview!.applied }
        : {}),
    };
  }, [tierSpec, slotMs, service.filters.afterHoursSurchargePct, promoPreview]);

  const slotDate = slotMs ? new Date(slotMs) : null;
  const lateNight = slotDate ? isLateNightSlot(slotDate) : false;
  const lateNightBlocked = lateNight && !service.filters.afterHoursAvailable;
  const sharedSpots = getSharedSpots(userDoc?.studentVerification?.institution);

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const tryPromo = async () => {
    if (!promoCode.trim() || !slotMs) return;
    try {
      const res = await callRedeemPromoCode({
        code: promoCode,
        serviceId: service.id,
        tier,
        scheduledStartMillis: slotMs,
      });
      if (res.data.applies) {
        setPromoPreview({
          discount: res.data.discountZARcents,
          total: res.data.totalZARcents,
          applied: promoCode.trim().toUpperCase(),
        });
        toast.success(`Promo applied: -${formatZAR(res.data.discountZARcents)}`);
      } else {
        setPromoPreview(null);
        toast.error(res.data.reason ?? 'Promo not valid.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const submit = async () => {
    if (!slotMs) return toast.error('Pick a day and time.');
    if (!previewPricing) return;
    if (!userDoc?.phoneNumber?.trim()) {
      toast.error('Add a phone number on your profile before booking.');
      return;
    }
    setSubmitting(true);
    try {
      const location: BookingLocation = {
        type: locType,
        ...(coord ? { geoPoint: { latitude: coord.latitude, longitude: coord.longitude } } : {}),
        ...(coord?.address ? { address: coord.address } : {}),
        ...(residence ? { residence } : {}),
        ...(locNotes ? { notes: locNotes } : {}),
      };
      const res = await callCreateBooking({
        serviceId: service.id,
        tier,
        scheduledStart: slotMs,
        location,
        customisationNotes: customNotes || undefined,
        promoCode: promoPreview?.applied,
        urgent,
      });
      onClose();
      toast.success(
        urgent
          ? 'Request sent — marked urgent for the creator.'
          : !PAYMENTS_ENABLED
            ? 'Request sent! Arrange payment once they accept.'
            : 'Request sent! You can pay as soon as they accept.',
      );
      navigate(`/bookings/${res.data.bookingId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Book ${service.title}`} size="lg">
      <SellerSafetyBadges
        preferences={sellerUser?.sellerProfile?.safetyPreferences}
        className="mb-4"
      />
      <div className="mb-6 flex gap-1.5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i <= step ? 'bg-makhe-ink' : 'bg-makhe-line'
            }`}
          />
        ))}
      </div>

      {step === 0 && (
        <div className="space-y-3">
          <p className="eyebrow">Step 01 · Tier</p>
          <h3 className="font-display text-2xl">Pick your tier</h3>
          {service.pricingTiers.map((t) => (
            <button
              key={t.tier}
              onClick={() => {
                setTier(t.tier);
                setSlotMs(null);
              }}
              className={`w-full rounded-md border p-4 text-left transition-colors ${
                tier === t.tier
                  ? 'border-makhe-ink bg-makhe-ink text-makhe-bg'
                  : 'border-makhe-line bg-makhe-paper hover:border-makhe-ink'
              }`}
            >
              <div className="flex items-center justify-between">
                <p className="font-display text-lg">{t.label}</p>
                <p className="font-mono">{formatZAR(t.priceZARcents)}</p>
              </div>
              <p
                className={`mt-1 text-[11px] uppercase tracking-widest ${
                  tier === t.tier ? 'text-makhe-bg/60' : 'text-makhe-muted'
                }`}
              >
                {t.durationMinutes} min
              </p>
            </button>
          ))}
        </div>
      )}

      {step === 1 && tierSpec && (
        <div className="space-y-5">
          <div>
            <p className="eyebrow">Step 02 · When</p>
            <h3 className="font-display text-2xl">Pick a slot in their hours</h3>
            <p className="mt-2 text-sm text-makhe-muted">
              Times follow {service.sellerSnapshot.displayName}’s weekly hours. They’ll confirm —
              or suggest another time if something clashes.
            </p>
          </div>

          <BookingSlotPicker
            sellerId={service.sellerId}
            durationMinutes={tierSpec.durationMinutes}
            valueMs={slotMs}
            onChange={setSlotMs}
          />

          {lateNightBlocked && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              <p className="font-medium text-amber-900">Late-night slot not available</p>
              <p className="mt-1 text-xs text-makhe-muted">
                {service.sellerSnapshot.displayName} doesn&apos;t accept bookings at this time.
                Pick another slot or choose a service that offers after-hours bookings.
              </p>
            </div>
          )}

          <label className="flex cursor-pointer items-start gap-3 rounded-md border border-makhe-line bg-makhe-paper p-3">
            <input
              type="checkbox"
              checked={urgent}
              onChange={(e) => setUrgent(e.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="block text-sm font-medium">Mark as urgent</span>
              <span className="mt-0.5 block text-xs text-makhe-muted">
                Signals you really need this time. It doesn’t skip their hours or bump other
                bookings — they still choose accept or propose another slot.
              </span>
            </span>
          </label>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="eyebrow">Step 03 · Where</p>
          <h3 className="font-display text-2xl">Where will it happen?</h3>
          <Select
            label="Type"
            value={locType}
            onChange={(e) => setLocType(e.target.value as OperatesFrom)}
            options={service.operatesFrom.map((o) => ({
              value: o,
              label: o.replace('_', ' '),
            }))}
          />
          {locType === 'buyer_location' && (
            <>
              <Input
                label="Residence / room"
                placeholder="Knockando, Room 314"
                value={residence}
                onChange={(e) => setResidence(e.target.value)}
              />
              {lateNight && (
                <p className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-makhe-muted">
                  Late-night residence calls carry extra risk. Consider a shared campus spot
                  instead — both parties must complete a safety check-in before the service
                  starts.
                </p>
              )}
            </>
          )}
          {locType === 'shared_spot' && sharedSpots.length > 0 && (
            <ul className="space-y-2 rounded-md border border-makhe-line p-3 text-xs text-makhe-muted">
              <li className="font-medium text-makhe-ink">Suggested meet-up spots</li>
              {sharedSpots.map((spot) => (
                <li key={spot.label}>
                  {spot.label}
                  {spot.notes ? ` — ${spot.notes}` : ''}
                </li>
              ))}
            </ul>
          )}
          <LocationPicker value={coord} onChange={setCoord} />
          <Textarea
            label="Notes for the seller"
            rows={2}
            value={locNotes}
            onChange={(e) => setLocNotes(e.target.value)}
            placeholder="Knock twice. The lift is broken."
          />
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <p className="eyebrow">Step 04 · Final touches</p>
          <h3 className="font-display text-2xl">Anything else?</h3>
          <Textarea
            label="Customisation notes"
            rows={3}
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            placeholder="Hair length, preferred colour, allergies, references…"
          />
          <div className="flex items-end gap-2">
            <Input
              label="Promo code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              placeholder="FIRST50"
              className="flex-1"
            />
            <Button variant="secondary" onClick={tryPromo} type="button">
              Apply
            </Button>
          </div>
          {previewPricing && <PriceBreakdown pricing={previewPricing} />}

          {PAYMENTS_ENABLED ? (
            <div>
              <p className="eyebrow">Payment</p>
              <div className="mt-3 rounded-md border border-makhe-line bg-makhe-paper p-3 text-sm">
                <p className="font-semibold">Escrow-protected</p>
                <p className="mt-0.5 text-xs text-makhe-muted">
                  Once the seller accepts, you pay and we hold your money safely until you
                  confirm the service is done — then we release it to the creator.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-makhe-line bg-makhe-paper p-3 text-sm">
              <p className="font-semibold">Payment isn’t live yet</p>
              <p className="mt-0.5 text-xs text-makhe-muted">
                Arrange payment directly with the seller once they accept. No money is collected
                by Makhe.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="mt-8 flex items-center justify-between border-t border-makhe-line pt-5">
        <Button variant="ghost" onClick={back} disabled={step === 0}>
          ← Back
        </Button>
        {step < 3 ? (
          <Button
            variant="ink"
            onClick={next}
            disabled={step === 1 && (!slotMs || lateNightBlocked)}
          >
            Continue →
          </Button>
        ) : (
          <Button variant="primary" onClick={submit} loading={submitting}>
            Send request →
          </Button>
        )}
      </div>
    </Modal>
  );
}
